import React, { useState, useMemo, useCallback } from "react";
import { ContractorService, ContractorVM, NotesEntityTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import AddNotesForm from "../notes/AddNotesForm";
import EditContractorForm from "./EditContratorForm";
import SubCategoryIcon from "../../assets/svgs/contractorIcons/subCategoryIcon";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import notesIcon from "../../assets/componentsIcons/dropdown/doc.svg";
import ArrowOutgoingIcon from "../../assets/svgs/arrows/arrowOutGoingIcon";
import { useNavigate } from "react-router-dom";
import UtilHelper from "../../utils/UtilHelper";
import { GetImages } from "../../utils/GetImages";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { getServiceSubTypeNames, getServiceTypeNames } from "../../utils/enumHelpers";
import { usePermissions } from "../../hooks/usePermissions";

const MemoizedEditContractorForm = React.memo(EditContractorForm);
const MemoizedConfirmationPopup = React.memo(ConfirmationPopup);
const MemoizedAddNotesForm = React.memo(AddNotesForm);
const MemoizedActionMenu = React.memo(ActionMenu);
const MemoizedSubCategoryIcon = React.memo(SubCategoryIcon);
const MemoizedArrowOutgoingIcon = React.memo(ArrowOutgoingIcon);

interface ContractorCardProps {
  id?: string;
  className?: string;
  listingData: ContractorVM;
  isFooter?: boolean;
  fetchData?: () => void;
  disableNavigation?: boolean;
}

const ContractorCardItem: React.FC<ContractorCardProps> = ({
  id,
  listingData,
  isFooter = true,
  className,
  fetchData,
  disableNavigation = false,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [editContractor, setEditContractor] = useState<ContractorVM | null>(null);
  const [deleteState, setDeleteState] = useState({ id: null as number | null, isOpen: false });
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [isNoteModalOpen, setNoteModalOpen] = useState(false);

  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  const fullName = useMemo(
    () => `${listingData.firstName} ${listingData.lastName}`,
    [listingData.firstName, listingData.lastName]
  );

  const contractorType = useMemo(
    () => listingData.isInHouse ? "In House" : "Third Party",
    [listingData.isInHouse]
  );


const categories = useMemo(
  () => getServiceTypeNames(listingData.serviceTypesIds, { humanReadable: true }),
  [listingData.serviceTypesIds]
);

const subCategories = useMemo(
  () =>
    getServiceSubTypeNames(
      listingData.serviceSubTypesIds,
      { humanReadable: true }
    ),
  [listingData.serviceSubTypesIds]
);



  const editContractorDetail = useCallback(async (contractorId: number) => {
    try {
      const response = await apiHelper(
        ContractorService.getApiContractor,
        { showNotification: false },
        contractorId
      );

      if (response?.data?.success) {
        setEditContractor(response.data.success);
        setModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching contractor details:", err);
    }
  }, []);

  const deleteContractor = useCallback(async () => {
    if (!deleteState.id) return;

    try {
      const response = await apiHelper(
        ContractorService.deleteApiContractor,
        {
          title: "Contractor Deleted",
          successMessage: `${fullName} deleted successfully`,
          showNotification: true,
          successIcon: <img src={GetImages.toastContractorIcon} alt="success" width="20" height="20" />
        },
        deleteState.id
      );

      if (response?.data?.success && fetchData) {
        await fetchData();
       // navigate('/contractors');
      }
    } catch (err) {
      console.error("Error deleting contractor:", err);
    } finally {
      setDeleteState({ id: null, isOpen: false });
    }
  }, [deleteState.id, fetchData]);

  const menuItems = useMemo(() => {
    const items = [];

    if (canWrite("Contractor")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: () => editContractorDetail(listingData.id),
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: () => setDeleteState({ id: listingData.id, isOpen: true }),
        isDivider: false,
        isDestructive: true,
      });

      items.push({ isDivider: true });

      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: () => {
          setSelectedEntityId(listingData.id);
          setNoteModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [listingData.id, editContractorDetail, canWrite]);

  const expensesDueDisplay = useMemo(() => {
    if (!listingData.formatted.ExpensesDue || listingData.formatted.ExpensesDue == 0) return null;
    return (
      <div className="highlight-value negative">
        <MemoizedArrowOutgoingIcon fill="#ef4444" className="highlight-icon" />
        <CurrencyTooltip
          content={listingData.formatted.ExpensesDueRaw || 0}
          formatted={listingData.formatted.ExpensesDue || 0}
        >
          <span className="highlight-text mx-2">
            {listingData.formatted.ExpensesDue}
          </span>
        </CurrencyTooltip>
      </div>
    );
  }, [listingData.formatted.ExpensesDue]);

  const dropdownTrigger = useMemo(() => (
    <button className="action-button">
      <img
        src={dropdownIcon}
        alt="More options"
        width={20}
        height={20}
        className="action-icon"
        loading="lazy"
      />
    </button>
  ), []);

  return (
    <div className={`listing-card contractor-card-container ${className}`}>
      <div
        className="listing-link"
        style={{
          textDecoration: "none",
          color: "inherit",
          cursor: disableNavigation ? "auto" : "pointer",
        }}
        onClick={() => {
          if (!disableNavigation) {
            navigate(`/contractors/contractor-details/${Number(listingData?.id)}`);
          }
        }}
      >
        <div className="listing-card-header">
          <div className="tenant-avatar">
            <div className="tenant-initials">{UtilHelper.getInitials(fullName)}</div>
          </div>
          <div className="listing-details">
            <div className="listing-info-container">
              <div className="listing-info">
                <h3 className="listing-address">{fullName}</h3>
                <p className="listing-type">{contractorType}</p>
                <p className="listing-type">
                  {listingData?.email || "N/A"}
                  <span className="contact-dot"> • </span>
                  <br className="contact-break" />
                  {listingData?.formatted?.PhoneNumber || "N/A"}
                </p>
              </div>

              <div className="financial-metrics">
                <div className="metric">
                  <p className="metric-value negative">{categories}</p>
                  <p className="metric-label">Category</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isFooter && (
        <>
          <div className="separator-horizontal" />
          <div className="listing-card-footer">
            <div className="listing-stats">
              <div className="stat">
                <MemoizedSubCategoryIcon fill="#4B5563" className="stat-icon" width="24" height="24" />
                <span className="stat-text">
                  {subCategories ? `Sub Categories: ${subCategories}` : `Contractor Type: ${categories}`}
                </span>
              </div>
            </div>

            <div className="footer-actions">
              <div className="footer-highlights">
                {expensesDueDisplay}
              </div>
              {menuItems.length > 0 && (
                <MemoizedActionMenu
                  menuItems={menuItems}
                  trigger={dropdownTrigger}
                />
              )}
            </div>
          </div>
        </>
      )}

      {
        isModalOpen && (
          <MemoizedEditContractorForm
            details={editContractor}
            isModalOpen={isModalOpen}
            setModalOpen={setModalOpen}
            fetchData={fetchData}
          />
        )
      }


      <MemoizedConfirmationPopup
        isOpen={deleteState.isOpen}
        title="Delete Contractor"
        message="Are you sure you want to delete this contractor?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={deleteContractor}
        onSecondaryAction={() => setDeleteState({ id: null, isOpen: false })}
        onClose={() => setDeleteState({ id: null, isOpen: false })}
        isPrimaryDestructive={true}
      />
      {
        isNoteModalOpen && (
          <MemoizedAddNotesForm
            entityId={selectedEntityId}
            entityTypeId={NotesEntityTypesEnum.Contractor}
            isModalOpen={isNoteModalOpen}
            setModalOpen={setNoteModalOpen}
          />
        )
      }

    </div>
  );
};

export default React.memo(ContractorCardItem, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.isFooter === nextProps.isFooter &&
    prevProps.listingData.id === nextProps.listingData.id &&
    prevProps.listingData.firstName === nextProps.listingData.firstName &&
    prevProps.listingData.lastName === nextProps.listingData.lastName &&
    prevProps.listingData.email === nextProps.listingData.email &&
    prevProps.listingData.phoneNumber === nextProps.listingData.phoneNumber &&
    prevProps.listingData.isInHouse === nextProps.listingData.isInHouse &&
    JSON.stringify(prevProps.listingData.formatted) === JSON.stringify(nextProps.listingData.formatted)
  );
});